import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import InputBase from '@material-ui/core/InputBase';
// import SearchIcon from '@material-ui/icons/Search';
import { injectIntl } from 'react-intl';
import { testIds as t } from 'ui-common/src/constants';
import { useSelector } from 'react-redux';
import SideNavSearchIcon from '../../ECMv6/assets/SideNavSearchIcon';
import SideNavSearchIconOpen from '../../ECMv6/assets/SideNavSearchIconOpen';
import SearchCloseIcon from '../../ECMv6/assets/SearchCloseIcon';
import { removeMenuById } from 'ui-home/src/utils/removeMenuByID/removeMenuByID';

const SearchBar = ({
  data,
  handleSearchData,
  intl,
  open,
  handleReset,
  searchKey,
  setSearchKey,
}) => {
  const {
    PAM_JIT_FOR_APPS_ENABLED: isJitForApps,
    PAM_EMERGENCY_ROLE_NAV_ENABLED: isEmergencyRolesUser,
    ISPM_SERVICES_ENABLED: isISPMEnabled,
  } = useSelector((state) => state.user.domainInfo || {});

  const useStyles = makeStyles((theme) => {
    const searchClass = {
      display: 'flex',
      flex: '0 0 auto',
      alignItems: 'center',
      position: 'relative',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: open ? 'var(--SIDEMENU-MENUITEM-BACKGROUND)' : 'transparent',
    };

    return {
      search: {
        ...searchClass,
        margin: open ? '0 12px 12px 12px' : '0 12px 9px',
        width: 'auto',
        minHeight: open ? 'auto' : '35px',
      },
      searchIcon: {
        padding: '0 6px',
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
      },
      inputRoot: {
        color: 'inherit',
        width: '100%',
        borderRadius: '4px',
        transition: 'border 3s',
        // border: open ? "1px solid #00245B" : "none", //the variable is nt available for this color code. Need to change later.
        border: open ? '1px solid var(--SIDEMENU-SEARCH-BORDER)' : 'none', // the variable is nt available for this color code. Need to change later.
        height: '32px',
      },
      inputInput: {
        margin: theme.spacing(1, 1, 1, 0),
        visibility: open ? 'visible' : 'hidden',
        // vertical padding + font size from searchIcon
        paddingLeft: 'calc(1em + 22px)',
        transition: theme.transitions.create('width'),
        width: '100%',
        '&::placeholder': {
          fontSize: '14px',
          color: 'var(--SIDEMENU-SUBTEXT-COLOR)',
          opacity: '1',
        },
        paddingRight: 10,
      },
      focus: {
        border: open ? '#00245B' : '',
        borderRadius: theme.shape.borderRadius,
      },
      clearIcon: {
        transform: 'translateX(-30px)',
        scale: 0.8,
        cursor: 'pointer',
        right: '-5px',
        position: 'relative',
      },
    };
  });
  const classes = useStyles();
  const searchData = (dataArray, searchTerm) =>
    dataArray.flatMap((obj) => {
      // Make sure translations are available for menu items. - in case new menus are getting added
      const getTranslatedText = (text) => intl.formatMessage({ id: `gen2.leftnav.label.${text}` });
      const objHasSearchTerm = Object.entries(obj).some(
        ([key]) =>
          key !== 'subMenu' &&
          getTranslatedText(obj.id).toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchedChildren = searchData(
        obj.subMenu && obj.subMenu?.length ? obj.subMenu : [],
        searchTerm
      );
      return matchedChildren.length > 0 || (objHasSearchTerm && !obj.subMenu)
        ? [
            {
              ...obj,
              subMenu: matchedChildren,
            },
          ]
        : [];
    });

  const handleChange = (event) => {
    const searchTerm = event.target.value;
    setSearchKey(searchTerm);

    // Remove ispm menu from the menu object if IS_ISPM_ENABLED flag is coming as false from the API.
    data = isISPMEnabled ? data : removeMenuById(data, 'ispm');

    /* Code to Show/Hide the PAM menus in the left navigation based on pamGen2 flag */
    const PRIVILEGED_ACCESS = 'privileged-access';
    const POLICIES = 'policies';
    const EMERGENCY_ACCESS = 'emergency-access';

    const MENU_TO_ALLOW_IF_PAM_ENABLED = {
      ASSET_SESSIONS: 'assets-sessions',
      PRIV_SVC_ACCT_MGMT: 'privileged-service-account-management',
    };

    const MENU_TO_ALLOW_IF_PAM_DISABLED = {
      NEW_PRIV_ACCESS: 'new-privileged-access',
      PRIV_SESSIONS: 'privileged-sessions',
      PAST_SESSIONS: 'past-sessions',
      PRIV_SVC_ACCT_MGMT: MENU_TO_ALLOW_IF_PAM_ENABLED.PRIV_SVC_ACCT_MGMT,
    };

    const PAM_MENUS_TO_DISALLOW_IF_JFA_ENABLED = ['manage-pam'];

    const ALL_MENUS_TO_DISALLOW_IF_JFA_ENABLED = [
      ...new Set(
        Object.values(MENU_TO_ALLOW_IF_PAM_ENABLED).concat(
          Object.values(MENU_TO_ALLOW_IF_PAM_DISABLED).concat(PAM_MENUS_TO_DISALLOW_IF_JFA_ENABLED)
        )
      ),
    ];

    const pamGen2Flag = window.localStorage.getItem('pamGen2');

    // Home
    data[0].subMenu.forEach((item) => {
      // Privileged Access
      if (item.id === PRIVILEGED_ACCESS) {
        const allowedIds = parseInt(pamGen2Flag)
          ? Object.values(MENU_TO_ALLOW_IF_PAM_ENABLED)
          : Object.values(MENU_TO_ALLOW_IF_PAM_DISABLED);
        item.subMenu = item.subMenu.filter((ele) =>
          isJitForApps
            ? !ALL_MENUS_TO_DISALLOW_IF_JFA_ENABLED.includes(ele.id)
            : allowedIds.includes(ele.id)
        );
      }
      // Emergency Access
      if (item.id === EMERGENCY_ACCESS) {
        if (!isEmergencyRolesUser) {
          item.subMenu = item.subMenu.filter((ele) => ele.id !== 'request-emergency-access-roles');
        }
      }
    });

    // Control Center
    if (data[3]) {
      data[3].subMenu = data[3].subMenu.filter((item) =>
        isJitForApps ? !ALL_MENUS_TO_DISALLOW_IF_JFA_ENABLED.includes(item.id) : item.id
      );
    }

    const result = searchData(data, searchTerm);
    if (searchTerm !== '') {
      handleSearchData(result, true);
    } else {
      handleSearchData(result, false);
    }
  };

  const handleClose = () => {
    setSearchKey('');
    handleReset();
  };

  const label = intl.formatMessage({ id: `gen2.leftnav.label.search` });

  return (
    <div className={classes.search}>
      {open ? (
        <div className={classes.searchIcon}>
          <SideNavSearchIconOpen style={{ color: 'var(--SIDEMENU-SUBTEXT-COLOR' }} />
        </div>
      ) : (
        <div className={classes.searchIcon}>
          <SideNavSearchIcon style={{ color: 'var(--SIDEMENU-SUBTEXT-COLOR' }} />
        </div>
      )}
      {open ? (
        <InputBase
          placeholder={label}
          onChange={handleChange}
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
            focused: classes.focus,
          }}
          inputProps={{ 'aria-label': label }}
          value={searchKey}
          data-testid={`${t.SIDE_MENU_SEARCH}`}
        />
      ) : null}
      {open && searchKey.length > 0 ? (
        <span className={classes.clearIcon} onClick={handleClose}>
          <SearchCloseIcon />
        </span>
      ) : null}
    </div>
  );
};

export default injectIntl(SearchBar);
